import React, { useMemo } from 'react';
import AvTable from '@components/Table/AvTable';
import { ChartClickInteraction } from '@components/Widgets/types';
import { AvTooltip, Filter } from '@fabric-ui/components';
import { generateInnerDefaultFormatter, HeadCellType, isNumberType } from '@fabric-ui/components/Table';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Collapse, Divider } from '@mui/material';
import { useGetHeadCellsWithRequestObject } from '@utils/RequestUtils';
import { styleNoUpperCase } from '../../../../../context/utils';
import { useFormattingRules } from '../../../../FormattingRules/hooks';
import { getFormattingRuleById } from '../../../../FormattingRules/utils';
import { defaultDrillDownHierarchy } from '../../../constants';
import { getRowStyle } from '../../../CustomDashboardsUtils';
import { Field, TableCategoryWidgetTypes } from '../../../types';
import { useDrillDownData } from '../../Interactions/useDrillDownData';
import { commonTableProps } from './constants';

export const ExpandedRow = ({
  isItemSelected,
  widget,
  globalFilters,
  setDrillDownValues,
  setWidget,
  activeIndex,
  selected,
  clickInteractions,
}: {
  widget: TableCategoryWidgetTypes;
  setDrillDownValues: (v) => void;
  setWidget: (v) => void;
  isItemSelected: boolean;
  activeIndex: number;
  globalFilters: Filter | undefined;
  selected: any[];
  clickInteractions?: ChartClickInteraction[];
}) => {
  const { data, isLoadingDrillDown, allDrillDownFields } = useDrillDownData({
    widget,
    enabled: isItemSelected,
    contextFilter: globalFilters,
    currentIndex: activeIndex,
  });

  return (
    <Collapse in={isItemSelected} timeout="auto" unmountOnExit>
      <ExpendedRowDrillDown
        data={data}
        isItemSelected={isItemSelected}
        selected={selected}
        clickInteractions={clickInteractions}
        isLoadingDrillDown={isLoadingDrillDown}
        allDrillDownFields={allDrillDownFields}
        setDrillDownValues={setDrillDownValues}
        globalFilters={globalFilters}
        widget={widget}
        setWidget={setWidget}
        activeIndex={activeIndex}
      />
    </Collapse>
  );
};

interface ExpendedRowDrillDownProps {
  data: any[];
  isLoadingDrillDown: boolean;
  allDrillDownFields: Field[];
  setDrillDownValues: (v) => void;
  widget: TableCategoryWidgetTypes;
  globalFilters: Filter | undefined;
  activeIndex: number;
  setWidget: (v) => void;
  selected: any[];
  clickInteractions?: ChartClickInteraction[];
  isItemSelected: boolean;
}

const arrowIcon = <ArrowDown style={{ ...iconSize(16), color: 'var(--color-content-interactive-secondary-default)' }} />;

const ExpendedRowDrillDown: React.FC<ExpendedRowDrillDownProps> = ({
  data,
  allDrillDownFields,
  setDrillDownValues,
  isLoadingDrillDown,
  widget,
  globalFilters,
  setWidget,
  activeIndex: currentIndex,
  selected,
  clickInteractions,
  isItemSelected,
}) => {
  const {
    requests: [request],
    drillDownHierarchy = defaultDrillDownHierarchy,
  } = widget;
  const { data: formattingRules = [] } = useFormattingRules();
  const { drillDownValues = [] } = drillDownHierarchy;
  const nextDrillDownField = allDrillDownFields[currentIndex + 1]?.name;
  const queryHeadCells = useGetHeadCellsWithRequestObject({
    ...request,
    select: { ...request.select, dims: allDrillDownFields[currentIndex] ? [allDrillDownFields[currentIndex]] : [] },
  });
  const activeRow = widget.drillDownHierarchy?.expendedIds || [];
  const headCells = useMemo(
    () =>
      [{ id: 'id', label: 'ID', hidden: true, isKey: true } as HeadCellType, ...queryHeadCells].map(cell => {
        const cellFieldStyle = widget.definition.custom.fieldStyle?.[cell.id];

        const formattingRuleById = getFormattingRuleById(
          cellFieldStyle?.formattingRuleId,
          formattingRules,
          widget?.definition.custom.applyFormattingToAll?.formattingRuleId
        );
        return cell.id === allDrillDownFields[currentIndex]?.name
          ? {
              ...cell,
              disableSortBy: true,
              sx: styleNoUpperCase,
              formatter: nextDrillDownField
                ? (value, row) => (
                    <Box
                      sx={{
                        ...(isNumberType(cell.type)
                          ? {
                              ...flex.row,
                              flexDirection: 'row-reverse',
                            }
                          : flex.row),
                        gap: '8px',
                        ...(!isItemSelected && activeRow[currentIndex] === row.id
                          ? { svg: { transform: 'rotate(-90deg)' } }
                          : { '.has-expandedRow:not(.expanded-row) & svg': { transform: 'rotate(-90deg)' } }),
                      }}>
                      {arrowIcon}
                      {generateInnerDefaultFormatter({ value, type: cell.type })}
                    </Box>
                  )
                : undefined,
            }
          : request.select.metrics.some(({ name }) => name === cell.id) &&
              clickInteractions?.length &&
              clickInteractions.some(v => v.isEnabledForCell({ dataKey: cell.id }))
            ? {
                ...cell,
                sx: styleNoUpperCase,
                rowStyle: ({ row }) =>
                  getRowStyle({
                    selected,
                    cell,
                    row,
                    columnFormatterType: cellFieldStyle?.columnFormatter,
                    formattingRule: formattingRuleById,
                  }),
                // eslint-disable-next-line react/no-unstable-nested-components
                formatter: (value, row) => (
                  <AvTooltip title={`Click to ${clickInteractions[0].title.toLowerCase()}`}>
                    <Box
                      sx={
                        isNumberType(cell.type)
                          ? {
                              ...flex.row,
                              flexDirection: 'row-reverse',
                            }
                          : flex.row
                      }
                      onClick={e => {
                        clickInteractions[0].onClick({ row, cell, currentIndex, e });
                      }}>
                      {generateInnerDefaultFormatter({ value, type: cell.type })}
                    </Box>
                  </AvTooltip>
                ),
                disableSortBy: true,
              }
            : { ...cell, sx: styleNoUpperCase };
      }),
    [queryHeadCells, drillDownHierarchy]
  );

  const expandedRow = (row, onChange, isItemSelected) => (
    <ExpandedRow
      isItemSelected={isItemSelected || activeRow[currentIndex] === row.id}
      widget={widget}
      setDrillDownValues={setDrillDownValues}
      setWidget={setWidget}
      globalFilters={globalFilters}
      activeIndex={currentIndex + 1}
      selected={selected}
      clickInteractions={clickInteractions}
    />
  );
  const onActiveCrossInner = v => {
    const hadVal = drillDownValues[currentIndex] !== undefined;
    if (setWidget && (hadVal ? true : drillDownHierarchy.fields[currentIndex])) {
      if (!v && activeRow[currentIndex]) {
        setWidget({
          ...widget,
          requests: [{ ...request, top: { ...request.top, offset: 0 } }],
          drillDownHierarchy: {
            ...drillDownHierarchy,
            expendedIds: activeRow.slice(0, currentIndex),
          },
        });
      }
      if (v) {
        const value = data.find(({ id }) => id === v)[allDrillDownFields[hadVal ? currentIndex : currentIndex].name];
        const newDrillDownValues = [...drillDownValues.slice(0, currentIndex), value];
        setWidget({
          ...widget,
          requests: [{ ...request, top: { ...request.top, offset: 0 } }],
          drillDownHierarchy: {
            ...drillDownHierarchy,
            activeIndex: hadVal ? currentIndex : currentIndex + 1,
            drillDownValues: newDrillDownValues,
            expendedIds: activeRow[currentIndex] ? activeRow.with(currentIndex, v) : [...activeRow, v],
          },
        });
      }
    }
  };

  return (
    <>
      <Divider />
      <AvTable
        sx={{ '.MuiTableRow-root:last-of-type>.MuiTableCell-root': { borderBottom: 'none' }, pl: 1 }}
        headCells={headCells}
        rows={data}
        resizable
        loading={isLoadingDrillDown}
        activeKey={activeRow[currentIndex]}
        onActive={onActiveCrossInner}
        size={widget.definition.custom.rowSize}
        {...(nextDrillDownField && { expandedRowObj: { component: expandedRow } })}
        {...commonTableProps}
        shouldDisplayNull
      />
    </>
  );
};
